import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button, Card, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Switch, Table, Tag,
  Typography,
} from 'antd';
import { EditOutlined, UndoOutlined } from '@ant-design/icons';
import { useDeleteBeerRule, useSaveBeerRule } from '../hooks';

const { Text } = Typography;

/** Only the values actually overridden on the beer; blanks inherit the global rule. */
export const ruleToForm = (beer) => {
  const { rules } = beer;
  const own = (key) => (rules.overridden.includes(key) ? rules[key] : null);
  return {
    minCoverWeeks: own('minCoverWeeks'),
    maxCoverWeeks: own('maxCoverWeeks'),
    leadTimeDays: own('leadTimeDays'),
    preferredBrewery: rules.preferredBrewery,
    gyleCodes: rules.gyleCodes.join(', '),
    auTransitUnits: rules.auTransitUnits == null ? null : Number(rules.auTransitUnits),
    include: rules.include,
    notes: rules.notes,
  };
};

/** Full beer-rule body, so a partial edit does not wipe the other fields. */
export const beerRulePayload = (beer, patch = {}) => ({
  beerName: beer.beerName,
  ...ruleToForm(beer),
  ...patch,
});

const hasOverride = (beer) => beer.rules.overridden.length > 0 || beer.rules.gyleCodes.length > 0
  || beer.rules.auTransitUnits != null || !beer.rules.include || !!beer.rules.notes;

const BeerRuleModal = ({
  beer, breweries, settings, onClose,
}) => {
  const [form] = Form.useForm();
  const saveRule = useSaveBeerRule();

  useEffect(() => {
    if (beer) form.setFieldsValue(ruleToForm(beer));
  }, [beer, form]);

  const submit = async () => {
    const values = await form.validateFields();
    await saveRule.mutateAsync({ beerName: beer.beerName, ...values });
    onClose();
  };

  return (
    <Modal
      title={beer ? `Rules for ${beer.beerName}` : ''}
      visible={!!beer}
      onCancel={onClose}
      onOk={submit}
      confirmLoading={saveRule.isLoading}
      okText="Save"
      destroyOnClose
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Text type="secondary">Leave a number blank to use the global rule.</Text>
        <Space wrap align="start" style={{ marginTop: 12 }}>
          <Form.Item name="minCoverWeeks" label="Min cover (weeks)">
            <InputNumber min={0} step={0.5} placeholder={String(settings.minCoverWeeks)} />
          </Form.Item>
          <Form.Item name="maxCoverWeeks" label="Max cover (weeks)">
            <InputNumber min={0} step={0.5} placeholder={String(settings.maxCoverWeeks)} />
          </Form.Item>
          <Form.Item name="leadTimeDays" label="Lead time (days)">
            <InputNumber min={0} placeholder={String(settings.leadTimeDays)} />
          </Form.Item>
        </Space>
        <Form.Item name="preferredBrewery" label="Only brew at">
          <Select allowClear placeholder="Any brewery" options={breweries.map((b) => ({ value: b, label: b }))} />
        </Form.Item>
        <Form.Item
          name="gyleCodes"
          label="Cin7 tank beer codes"
          tooltip="Comma-separated gyle prefixes (e.g. TI for TI19-2026) or tank beer names, for gyles that don't match automatically"
        >
          <Input placeholder="e.g. TI, Tiny Hazy" />
        </Form.Item>
        <Form.Item
          name="auTransitUnits"
          label="AU transit (units)"
          tooltip="Replaces the Cin7 AU transit unit count on the brew plan. Does not change total volume or weeks."
        >
          <InputNumber min={0} style={{ width: '100%' }} placeholder="Use Cin7" />
        </Form.Item>
        <Form.Item name="include" label="Plan brews for this beer" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

BeerRuleModal.propTypes = {
  beer: PropTypes.shape({ beerName: PropTypes.string, rules: PropTypes.shape({}) }),
  breweries: PropTypes.arrayOf(PropTypes.string).isRequired,
  settings: PropTypes.shape({
    minCoverWeeks: PropTypes.number,
    maxCoverWeeks: PropTypes.number,
    leadTimeDays: PropTypes.number,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

BeerRuleModal.defaultProps = { beer: null };

const BeerRules = ({ plan, canEdit }) => {
  const [editing, setEditing] = useState(null);
  const deleteRule = useDeleteBeerRule();
  const breweries = (plan.settings.tanks || []).map((t) => t.brewery);
  const own = (beer, key) => beer.rules.overridden.includes(key);

  const cell = (key, suffix) => (value, beer) => (
    <Text strong={own(beer, key)} type={own(beer, key) ? undefined : 'secondary'}>
      {`${value}${suffix}`}
    </Text>
  );

  const columns = [
    { title: 'Beer', dataIndex: 'beerName' },
    { title: 'Min cover', dataIndex: ['rules', 'minCoverWeeks'], render: cell('minCoverWeeks', ' wk') },
    { title: 'Max cover', dataIndex: ['rules', 'maxCoverWeeks'], render: cell('maxCoverWeeks', ' wk') },
    { title: 'Lead time', dataIndex: ['rules', 'leadTimeDays'], render: cell('leadTimeDays', ' days') },
    {
      title: 'Brewery',
      dataIndex: ['rules', 'preferredBrewery'],
      render: (b) => b || <Text type="secondary">Any</Text>,
    },
    {
      title: 'Tank codes',
      dataIndex: ['rules', 'gyleCodes'],
      render: (codes) => codes.map((c) => <Tag key={c}>{c}</Tag>),
    },
    {
      title: 'AU transit',
      dataIndex: ['rules', 'auTransitUnits'],
      render: (units) => (units == null ? <Text type="secondary">Cin7</Text> : units),
    },
    {
      title: 'Planned',
      dataIndex: ['rules', 'include'],
      render: (include) => (include ? 'Yes' : <Tag>Excluded</Tag>),
    },
    { title: 'Notes', dataIndex: ['rules', 'notes'], ellipsis: true },
    {
      title: '',
      width: 110,
      render: (_, beer) => (canEdit ? (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => setEditing(beer)} />
          {hasOverride(beer) && (
            <Popconfirm title="Use the global rules for this beer?" onConfirm={() => deleteRule.mutate(beer.beerName)}>
              <Button size="small" icon={<UndoOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ) : null),
    },
  ];

  return (
    <Card
      className="forecasting-section"
      title="Per-beer overrides"
      extra={<Text type="secondary">Bold values are set on the beer; grey ones inherit the global rule</Text>}
    >
      <Table
        rowKey="beerName"
        size="small"
        pagination={false}
        columns={columns}
        dataSource={[...plan.beers].sort((a, b) => a.beerName.localeCompare(b.beerName))}
      />
      <BeerRuleModal
        beer={editing}
        breweries={breweries}
        settings={plan.settings}
        onClose={() => setEditing(null)}
      />
    </Card>
  );
};

BeerRules.propTypes = {
  plan: PropTypes.shape({
    beers: PropTypes.arrayOf(PropTypes.object),
    settings: PropTypes.shape({ tanks: PropTypes.arrayOf(PropTypes.object) }),
  }).isRequired,
  canEdit: PropTypes.bool.isRequired,
};

export default BeerRules;
